import { distance } from "@/libs/geometry/geometry"
import { getImageSourceDimensions, type ImageSource } from "@/libs/image"
import { averagePoint } from "@/libs/math"
import type { NormalizedLandmark } from "@mediapipe/tasks-vision"
import { Graph } from "../../framework"
import type { FaceDetection } from "../types"

type Point = { x: number; y: number }

const normalizeToPixel = (
  landmark: NormalizedLandmark,
  frameWidth: number,
  frameHeight: number,
): Point => ({
  x: landmark.x * frameWidth,
  y: landmark.y * frameHeight,
})

export interface ScoreTransformSettings {
  norm_shift: number
  norm_coef: number
  tanh_shift: number
  tanh_coef: number
  range_coef: number
  invert_score_flag: boolean
}

// Ported from https://github.com/hautaiou/saas-ml-algorithms/blob/dev/hautai/saas/algorithms/selfie/quality/resolution.py#L10
const FRONT_SCORE_TRANSFORM_SETTINGS: ScoreTransformSettings = {
  norm_shift: 0,
  norm_coef: 250,
  tanh_shift: 0.4,
  tanh_coef: 5,
  range_coef: 51,
  invert_score_flag: false,
}

const makeScore = (value: number, settings: ScoreTransformSettings): number => {
  const normalized = (value - settings.norm_shift) / settings.norm_coef
  const tanhValue = Math.tanh(settings.tanh_coef * (normalized - settings.tanh_shift))
  let score = tanhValue * settings.range_coef
  if (settings.invert_score_flag) {
    score = settings.range_coef - score
  }
  return score
}

export interface ResolutionMetrics {
  frameWidth: number
  frameHeight: number
  noseHeightPx: number
  eyeWidthPx: number
  eyeHeightPx: number
  leftEyeOpenness: number
  rightEyeOpenness: number
  resolutionScore: number
  fullFaceVisible: boolean
}

export const create = async <I extends unknown[], O extends unknown[]>(graph: Graph<I, O>) => {
  return graph.node<[ImageSource, FaceDetection], [ResolutionMetrics]>({
    process: async (ctx) => {
      const frame = ctx.in(0)
      const faceDetection = ctx.in(1)

      // Validate inputs and ensure we have enough landmarks (we need at least index 47)
      if (
        !frame ||
        !faceDetection ||
        !faceDetection.landmarks ||
        faceDetection.landmarks.length < 48
      ) {
        ctx.out(0, {
          frameWidth: 0,
          frameHeight: 0,
          noseHeightPx: 0,
          eyeWidthPx: 0,
          eyeHeightPx: 0,
          leftEyeOpenness: 0,
          rightEyeOpenness: 0,
          resolutionScore: 0,
          fullFaceVisible: false,
        })
        return
      }

      const { width: frameWidth, height: frameHeight } = getImageSourceDimensions(frame)

      // Convert all landmarks to pixel coordinates.
      const landmarksPx: Point[] = faceDetection.landmarks.map((lm) =>
        normalizeToPixel(lm, frameWidth, frameHeight),
      )

      // --- Determine if full face is visible ---
      const xs = landmarksPx.map((p) => p.x)
      const ys = landmarksPx.map((p) => p.y)
      const minX = Math.min(...xs)
      const maxX = Math.max(...xs)
      const minY = Math.min(...ys)
      const maxY = Math.max(...ys)
      const fullFaceVisible = minX >= 0 && minY >= 0 && maxX <= frameWidth && maxY <= frameHeight

      // --- Calculate Nose Height ---
      const noseTop = landmarksPx[168]
      const noseBottom = landmarksPx[4]
      const noseDistance = distance(noseTop, noseBottom)
      const noseHeightPx = 0.62 * noseDistance

      // --- Calculate Left Eye Metrics ---
      const leftEyePt1 = averagePoint(landmarksPx[133], landmarksPx[33])
      const leftEyePt2 = averagePoint(landmarksPx[159], landmarksPx[145])
      const leftEyeHeight = Math.abs(leftEyePt1.y - leftEyePt2.y)
      const leftEyeWidth = Math.abs(landmarksPx[42].x - landmarksPx[45].x)

      // --- Calculate Right Eye Metrics ---
      const rightEyePt1 = averagePoint(landmarksPx[386], landmarksPx[387])
      const rightEyePt2 = averagePoint(landmarksPx[374], landmarksPx[375])
      const rightEyeWidth = Math.abs(landmarksPx[362].x - landmarksPx[263].x)
      const rightEyeHeight = Math.abs(rightEyePt1.y - rightEyePt2.y)

      // For a front face, use the average of left and right eye metrics.
      const eyeHeightPx = Math.round((leftEyeHeight + rightEyeHeight) / 2)
      const eyeWidthPx = Math.round((leftEyeWidth + rightEyeWidth) / 2)

      // --- Estimate Eye Openness (raw ratios) ---
      const leftEyeOpenness =
        leftEyeHeight > 5 ? parseFloat((leftEyeHeight / leftEyeWidth).toFixed(2)) : 0
      const rightEyeOpenness =
        rightEyeHeight > 5 ? parseFloat((rightEyeHeight / rightEyeWidth).toFixed(2)) : 0

      // --- Compute Resolution Scores ---
      // Transform nose height and eye width into scores.
      const noseResolutionScore = makeScore(noseHeightPx, FRONT_SCORE_TRANSFORM_SETTINGS)
      const eyeResolutionScore = makeScore(eyeWidthPx, FRONT_SCORE_TRANSFORM_SETTINGS)
      const resolutionScore = Math.round((noseResolutionScore + eyeResolutionScore) / 2)

      const result: ResolutionMetrics = {
        frameWidth,
        frameHeight,
        noseHeightPx: Math.round(noseHeightPx),
        eyeWidthPx,
        eyeHeightPx,
        leftEyeOpenness,
        rightEyeOpenness,
        resolutionScore,
        fullFaceVisible,
      }

      ctx.out(0, result)
    },
  })
}
